import React, {
    useEffect,
    useRef,
    useState,
    useImperativeHandle,
    forwardRef,
} from "react";
import "./DatePartsInput.css";

/**
 * 自前 3 分割日付入力 (YYYY / MM / DD)
 *
 * 仕様:
 *  - 年4桁完成 -> 月へ自動フォーカス
 *  - 月2桁完成 -> 日へ自動フォーカス
 *  - 日2桁完成 -> その場で止まる (次フィールドへの自動遷移はしない)
 *  - 空欄で Backspace -> 1 つ前のセグメントへフォーカス
 *  - "/" または "-" キーで次のセグメントへ
 *  - 任意セグメントをクリックでそこだけ修正可
 *  - 親には完成した "YYYY-MM-DD" のみ onChange で通知 (途中状態は通知しない)
 *  - 空に戻したときは "" を通知
 *
 * props:
 *   value     : "YYYY-MM-DD" または ""
 *   onChange  : (value: string) => void
 *   onBlur    : (e: { target: { value: string } }) => void   // 任意。完成済み or "" を渡す
 *   required  : boolean
 *   className : string  追加クラス
 *   name      : string  HTML name (フォーム送信用、任意)
 *   min       : "YYYY-MM-DD"  これより前の日付は選択・入力できない（任意）
 *   max       : "YYYY-MM-DD"  これより後の日付は選択・入力できない（任意）
 */
const CAL_WEEKDAYS = ["日", "月", "火", "水", "木", "金", "土"];

/** 和暦（令和8 など）。取得できない場合は空文字。 */
function calEra(year, month) {
  try {
    const d = new Date(year, (month || 1) - 1, 1);
    const s = new Intl.DateTimeFormat("ja-JP-u-ca-japanese", {
      era: "long",
      year: "numeric",
    }).format(d);
    return s.replace(/年.*$/, ""); // "令和8年" → "令和8"
  } catch (_) {
    return "";
  }
}
const calYmd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** カレンダー（タイトルを大きく表示）。value/onSelect は "YYYY-MM-DD"。
 *  min / max を渡すと、その範囲外の日付は押せなくなる。 */
function CalPopup({ value, onSelect, onClose, min, max }) {
  const today = new Date();
  const sel = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
  const [y, setY] = useState(sel ? Number(sel[1]) : today.getFullYear());
  const [mo, setMo] = useState(sel ? Number(sel[2]) : today.getMonth() + 1);

  const goMonth = (delta) => {
    let nm = mo + delta;
    let ny = y;
    if (nm < 1) { nm = 12; ny -= 1; }
    if (nm > 12) { nm = 1; ny += 1; }
    setMo(nm); setY(ny);
  };

  const firstDow = new Date(y, mo - 1, 1).getDay();
  const start = new Date(y, mo - 1, 1 - firstDow);
  const cells = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    cells.push(d);
  }
  const todayStr = calYmd(today);

  return (
    <div className="cal-popup" role="dialog" aria-label="カレンダー">
      <div className="cal-header">
        <button type="button" className="cal-nav-btn" onClick={() => goMonth(-1)} aria-label="前月">‹</button>
        <div className="cal-title">{y}年（{calEra(y, mo)}年）{mo}月</div>
        <button type="button" className="cal-nav-btn" onClick={() => goMonth(1)} aria-label="翌月">›</button>
      </div>
      <div className="cal-grid cal-weekdays">
        {CAL_WEEKDAYS.map((w, i) => (
          <div key={w} className={`cal-wd${i === 0 ? " cal-sun" : ""}${i === 6 ? " cal-sat" : ""}`}>{w}</div>
        ))}
      </div>
      <div className="cal-grid">
        {cells.map((d) => {
          const ds = calYmd(d);
          const other = d.getMonth() !== mo - 1 || d.getFullYear() !== y;
          const dow = d.getDay();
          const disabled = (Boolean(min) && ds < min) || (Boolean(max) && ds > max);
          const cls = [
            "cal-cell",
            other ? "cal-other" : "",
            ds === value ? "cal-selected" : "",
            ds === todayStr ? "cal-today" : "",
            dow === 0 ? "cal-sun" : "",
            dow === 6 ? "cal-sat" : "",
            disabled ? "cal-disabled" : "",
          ].filter(Boolean).join(" ");
          return (
            <button
              key={ds}
              type="button"
              className={cls}
              disabled={disabled}
              onClick={() => { onSelect(ds); onClose(); }}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
      <div className="cal-footer">
        <button type="button" className="cal-foot-btn" onClick={() => { onSelect(""); onClose(); }}>クリア</button>
        <button
          type="button"
          className="cal-foot-btn"
          onClick={() => { setY(today.getFullYear()); setMo(today.getMonth() + 1); }}
        >
          今日
        </button>
      </div>
    </div>
  );
}

const DatePartsInput = forwardRef(function DatePartsInput(
    { value, onChange, onBlur, onComplete, required, className, name, min, max },
    ref
) {
    const [yearStr, setYearStr] = useState("");
    const [monthStr, setMonthStr] = useState("");
    const [dayStr, setDayStr] = useState("");
    const [showCal, setShowCal] = useState(false);

    const yearRef = useRef(null);
    const monthRef = useRef(null);
    const dayRef = useRef(null);
    const wrapperRef = useRef(null);
    const hiddenDateRef = useRef(null);

    useImperativeHandle(ref, () => ({
        focus: () => yearRef.current?.focus(),
    }));

    useEffect(() => {
        if (!value) {
            setYearStr("");
            setMonthStr("");
            setDayStr("");
            return;
        }
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
        if (m) {
            setYearStr(m[1]);
            setMonthStr(m[2]);
            setDayStr(m[3]);
        }
    }, [value]);

    const emit = (y, m, d) => {
        if (y.length === 4 && m.length === 2 && d.length === 2) {
            onChange(`${y}-${m}-${d}`);
        } else if (!y && !m && !d) {
            onChange("");
        }
    };

    const handleYear = (e) => {
        const v = e.target.value.replace(/\D/g, "").slice(0, 4);
        setYearStr(v);
        emit(v, monthStr, dayStr);
        if (v.length === 4) {
            monthRef.current?.focus();
            monthRef.current?.select();
        }
    };

    const handleMonth = (e) => {
        const v = e.target.value.replace(/\D/g, "").slice(0, 2);
        setMonthStr(v);
        emit(yearStr, v, dayStr);
        if (v.length === 2) {
            dayRef.current?.focus();
            dayRef.current?.select();
        }
    };

    const handleDay = (e) => {
        const v = e.target.value.replace(/\D/g, "").slice(0, 2);
        setDayStr(v);
        emit(yearStr, monthStr, v);
        if (
            v.length === 2 &&
            yearStr.length === 4 &&
            monthStr.length === 2 &&
            typeof onComplete === "function"
        ) {
            onComplete(`${yearStr}-${monthStr}-${v}`);
        }
    };

    const handleBlur = (e) => {
        if (wrapperRef.current && e.relatedTarget && wrapperRef.current.contains(e.relatedTarget)) {
            return;
        }
        let composed =
            yearStr.length === 4 && monthStr.length === 2 && dayStr.length === 2
                ? `${yearStr}-${monthStr}-${dayStr}`
                : "";
        // 手入力で min〜max の範囲外が入れられた場合は取り消す
        // （カレンダー・ネイティブピッカーは min/max で塞いであるが、直接入力は通ってしまうため）
        if (composed && ((min && composed < min) || (max && composed > max))) {
            setYearStr("");
            setMonthStr("");
            setDayStr("");
            composed = "";
            onChange("");
        }
        if (onBlur) {
            onBlur({ target: { value: composed, name: name || "" } });
        }
    };

    // カレンダーアイコン: タイトルを大きく表示する自作カレンダーを開閉する
    const openPicker = () => setShowCal((v) => !v);

    const handleCalSelect = (v) => {
        if (!v) {
            setYearStr("");
            setMonthStr("");
            setDayStr("");
            onChange("");
            return;
        }
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
        if (!m) return;
        if ((min && v < min) || (max && v > max)) return;
        setYearStr(m[1]);
        setMonthStr(m[2]);
        setDayStr(m[3]);
        onChange(v);
        if (typeof onComplete === "function") onComplete(v);
    };

    const handleHiddenChange = (e) => {
        const newVal = e.target.value;
        if (!newVal) return;
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(newVal);
        if (!m) return;
        if ((min && newVal < min) || (max && newVal > max)) return;
        setYearStr(m[1]);
        setMonthStr(m[2]);
        setDayStr(m[3]);
        onChange(newVal);
        if (typeof onComplete === "function") {
            onComplete(newVal);
        }
    };

    const handleKey = (which) => (e) => {
        if (e.key === "Backspace" && e.target.value === "") {
            if (which === "month") {
                e.preventDefault();
                yearRef.current?.focus();
            } else if (which === "day") {
                e.preventDefault();
                monthRef.current?.focus();
            }
        } else if (e.key === "/" || e.key === "-") {
            e.preventDefault();
            if (which === "year") {
                monthRef.current?.focus();
                monthRef.current?.select();
            } else if (which === "month") {
                dayRef.current?.focus();
                dayRef.current?.select();
            }
        } else if (e.key === "ArrowRight" && e.target.selectionStart === e.target.value.length) {
            if (which === "year") {
                e.preventDefault();
                monthRef.current?.focus();
                monthRef.current?.select();
            } else if (which === "month") {
                e.preventDefault();
                dayRef.current?.focus();
                dayRef.current?.select();
            }
        } else if (e.key === "ArrowLeft" && e.target.selectionStart === 0) {
            if (which === "month") {
                e.preventDefault();
                yearRef.current?.focus();
            } else if (which === "day") {
                e.preventDefault();
                monthRef.current?.focus();
            }
        }
    };

    return (
        <div
            ref={wrapperRef}
            className={`date-parts-input ${className || ""}`}
            onBlur={handleBlur}
        >
            <input
                ref={yearRef}
                type="text"
                inputMode="numeric"
                value={yearStr}
                onChange={handleYear}
                onKeyDown={handleKey("year")}
                onFocus={(e) => e.target.select()}
                placeholder="YYYY"
                className="dp-year"
                maxLength={4}
                required={required}
                aria-label="年"
            />
            <span className="dp-sep">/</span>
            <input
                ref={monthRef}
                type="text"
                inputMode="numeric"
                value={monthStr}
                onChange={handleMonth}
                onKeyDown={handleKey("month")}
                onFocus={(e) => e.target.select()}
                placeholder="MM"
                className="dp-month"
                maxLength={2}
                required={required}
                aria-label="月"
            />
            <span className="dp-sep">/</span>
            <input
                ref={dayRef}
                type="text"
                inputMode="numeric"
                value={dayStr}
                onChange={handleDay}
                onKeyDown={handleKey("day")}
                onFocus={(e) => e.target.select()}
                placeholder="DD"
                className="dp-day"
                maxLength={2}
                required={required}
                aria-label="日"
            />
            <button
                type="button"
                className="dp-picker-btn"
                onClick={openPicker}
                tabIndex={-1}
                aria-label="カレンダーを開く"
            >
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
            </button>
            <input
                ref={hiddenDateRef}
                type="date"
                className="dp-hidden-date"
                value={value || ""}
                onChange={handleHiddenChange}
                min={min || undefined}
                max={max || undefined}
                tabIndex={-1}
                aria-hidden="true"
            />
            {showCal && (
                <>
                    <div className="cal-backdrop" onClick={() => setShowCal(false)} />
                    <CalPopup
                        value={value}
                        onSelect={handleCalSelect}
                        onClose={() => setShowCal(false)}
                        min={min}
                        max={max}
                    />
                </>
            )}
        </div>
    );
});

export default DatePartsInput;
